<template>
    <div class="body_sales_analysis">
        <div v-if="isLoading" class="state_message">
            <p>Cargando análisis…</p>
        </div>

        <div v-else-if="error" class="state_message">
            <p class="error_text">{{ error }}</p>
        </div>

        <div v-else-if="analysis" class="analysis_grid">
            <HeadlineMonthCard class="cell cell--top-left"
                :period-label="analysis.period_label"
                :headline="analysis.headline" />

            <ClientsKpiCard class="cell cell--top-right"
                :kpis="analysis.clients_kpis" />

            <StoresDistributionCard v-if="hasStoresDistribution"
                class="cell cell--bottom-left"
                :distributions="analysis.stores_distribution"
                @focus-category="onFocusCategory"
                @blur-category="onBlurCategory" />

            <ClientsRankingCard class="cell cell--bottom-right"
                v-model="clientsQueryModel"
                :clients="filteredClients"
                :highlighted-clients="highlightedClients"
                @client-click="openClientDetail" />
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import HeadlineMonthCard from "./HeadlineMonthCard.vue";
import ClientsKpiCard from "./ClientsKpiCard.vue";
import StoresDistributionCard from "./StoresDistributionCard.vue";
import ClientsRankingCard from "./ClientsRankingCard.vue";
import ModalFullHeightRight from "@/components/modals/ModalFullHeightRight.vue";
import HeaderClientDetail from "./clientDetail/HeaderClientDetail.vue";
import BodyClientDetail from "./clientDetail/BodyClientDetail.vue";

export default {
    name: "BodySalesAnalysis",
    components: {
        HeadlineMonthCard,
        ClientsKpiCard,
        StoresDistributionCard,
        ClientsRankingCard,
    },
    // Componente orquestador del feature: lee del módulo salesAnalysis del
    // store y reparte slices a las cards presentacionales. No expone props
    // — el modal pasa `bodyProps: null` y la lectura es vía Vuex.
    data() {
        return {
            // Set de nombres de clientes asociados al atributo hovereado
            // en StoresDistributionCard. `null` = sin foco activo, ranking
            // se ve normal. Cuando hay foco, `ClientsRankingCard` resalta
            // los del set y atenúa el resto.
            highlightedClients: null,
        };
    },
    computed: {
        ...mapState("salesAnalysis", {
            analysis: "data",
            isLoading: "isLoading",
            error: "error",
            clientsQuery: "clientsQuery",
            analysisMonth: "month",
        }),
        ...mapGetters("salesAnalysis", ["filteredClients"]),
        // Cuando el caller no provee categories (modo cuentas/clientes sin
        // categorías dinámicas configuradas), el backend devuelve `[]` y la
        // card no se monta — evita un título "...por null" y donut vacío.
        hasStoresDistribution() {
            const stores = this.analysis?.stores_distribution;
            return Array.isArray(stores) && stores.length > 0
                && stores.some((d) => Array.isArray(d.items) && d.items.length > 0);
        },
        // Bridge entre el v-model presentational del ranking card y el store
        // global. El padre asume el acople a Vuex; la card se queda pura.
        clientsQueryModel: {
            get() { return this.clientsQuery; },
            set(value) { this.setClientsQuery(value); },
        },
    },
    methods: {
        ...mapMutations("salesAnalysis", { setClientsQuery: "SET_CLIENTS_QUERY" }),
        ...mapActions("clientDetail", {
            openClientDetailAction: "openClientDetail",
            closeClientDetailAction: "closeClientDetail",
            changeClientRangePeriod: "changeRangePeriod",
        }),

        // Highlight cruzado: el StoresDistributionCard emite `focus-category`
        // con el atributo hovereado; computamos qué clientes del ranking
        // están asociados a ese atributo (cada client trae su `categories`
        // map del backend) y armamos un Set que pasamos a ClientsRankingCard.
        onFocusCategory({ category_id, value }) {
            const clients = this.analysis?.clients_ranking || [];
            const matchSet = new Set();
            for (const c of clients) {
                const values = c.categories && c.categories[category_id];
                if (Array.isArray(values) && values.includes(value)) {
                    matchSet.add(c.name);
                }
            }
            this.highlightedClients = matchSet;
        },
        onBlurCategory() {
            this.highlightedClients = null;
        },

        // El ranking emite `{ name, amount, share }`. Resolvemos el ci_id
        // dentro de la action (`openClientDetail` con alias) y abrimos el
        // segundo drawer encima del modal de análisis — Buefy soporta stack
        // de modales y propaga foco al último abierto.
        openClientDetail(client) {
            if (!client || !client.name || !this.analysisMonth) return;

            // V1: el ranking sólo emerge desde el tab Ventas — counterpart
            // es siempre Cliente (document_type='C'). Cuando otros tabs
            // (Gastos: Proveedor='P') usen este flow, el caller debería
            // determinar el documentType. Para esto centralizamos el valor
            // en una sola línea acá.
            const documentType = "C";

            this.openClientDetailAction({
                alias: client.name,
                documentType,
                monthIso: this.analysisMonth,
                obligationName: "Otros",
            });

            this.$buefy.modal.open({
                parent: this,
                hasModalCard: true,
                customClass: "wide-summary-modal",
                canCancel: ["escape", "outside"],
                component: ModalFullHeightRight,
                props: {
                    headerComp: HeaderClientDetail,
                    bodyComp: BodyClientDetail,
                    headerProps: { fallbackAlias: client.name },
                    bodyProps: null,
                    // El theme del tab Ventas se propaga al segundo drawer
                    // para que los tokens --report-* del subárbol respeten
                    // la paleta del tab activo.
                    theme: "ventas",
                    // Width menor que el primer drawer (85%) para que se
                    // perciba visualmente que es un segundo nivel apilado
                    // encima del análisis del mes — deja ver una franja
                    // del modal padre y refuerza la jerarquía del drill.
                    width: "70%",
                },
                events: {
                    closed: this.closeClientDetailAction,
                    "update-range-period": this.changeClientRangePeriod,
                },
            });
        },
    },
};
</script>

<style scoped lang="scss">
// Cadena de altura: el body absorbe lo que le da .modal_back_caa (flex:1)
// y propaga vía grid → cells → cards. El scroll vive DENTRO de cada card,
// nunca acá. `min-height: 0` en body+grid+cell es lo que permite que el
// constraint se respete a través de los layers.
.body_sales_analysis {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.state_message {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px 16px;
    text-align: center;
    color: var(--report-text-muted);
    font-size: 14px;
}

.error_text {
    color: var(--report-negative);
}

.analysis_grid {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: 1fr 1fr;
    // Asimetría vertical intencional: la fila superior se autodimensiona a
    // su contenido (KPIs y headline son densos y cortos) hasta un techo de
    // 320px; si excede, el card scrollea internamente. La inferior toma el
    // resto con `1fr`, dándole espacio a la distribución de tiendas y al
    // ranking de clientes — que es donde está la lectura larga.
    grid-template-rows: minmax(auto, 320px) 1fr;
    gap: 24px;
}

.cell {
    // Ambos min-* son requisito para que la cell pueda ser más corta que su
    // contenido natural — sin esto el grid pide la altura intrínseca y se
    // pierde el clamp.
    min-width: 0;
    min-height: 0;
}
</style>
